/**
 * PasskeySettings — listet alle registrierten Passkeys des aktuellen Users
 * und erlaubt Anlegen/Löschen. Wird in /profile eingebunden.
 *
 *  - "Neuen Passkey anlegen" → triggert WebAuthn-Registration → BE persistiert
 *  - Liste: Label, Erstellungsdatum, letzte Nutzung, Löschen-Button
 *  - Wenn der Browser keine Passkeys unterstützt, wird ein Hinweis angezeigt
 *    statt der Button.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Trash2, Plus, Loader2, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isPasskeySupported, enrollPasskey, listPasskeys, deletePasskey,
} from "@/lib/passkey";

export default function PasskeySettings() {
  const supported = isPasskeySupported();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const reload = async () => {
    if (!supported) return;
    setLoading(true);
    try {
      setCreds(await listPasskeys());
    } catch {
      // silent — likely just empty
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const handleEnroll = async () => {
    if (enrolling) return;
    setEnrolling(true);
    try {
      const label = window.prompt(
        "Bezeichnung für diesen Passkey (z.B. 'iPhone von Max'):",
        navigator.userAgent.includes("iPhone") ? "iPhone" :
        navigator.userAgent.includes("Android") ? "Android-Handy" :
        navigator.userAgent.includes("Mac") ? "Mac" :
        navigator.userAgent.includes("Windows") ? "Windows-PC" : "Mein Gerät",
      );
      if (!label) { setEnrolling(false); return; }
      await enrollPasskey(label.trim().slice(0, 80) || "Mein Gerät");
      toast.success("Passkey erfolgreich angelegt", {
        description: "Beim nächsten Login einfach 'Face ID / Touch ID' wählen.",
      });
      await reload();
    } catch (e) {
      toast.error(e?.message || "Passkey-Anlage fehlgeschlagen");
    } finally {
      setEnrolling(false);
    }
  };

  const handleDelete = async (cred) => {
    if (!window.confirm(`Passkey "${cred.label}" wirklich löschen?\n\nDanach ist auf diesem Gerät kein Face-ID-/Touch-ID-Login mehr möglich.`)) return;
    try {
      await deletePasskey(cred.id);
      toast.success("Passkey gelöscht");
      setCreds((prev) => prev.filter((c) => c.id !== cred.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  if (!supported) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3" data-testid="passkey-unsupported">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-800">
          <Fingerprint className="text-zinc-500" size={16} />
          <h2 className="font-display text-sm font-semibold text-zinc-400 uppercase tracking-widest">Passkeys / Face ID</h2>
        </div>
        <p className="text-sm text-zinc-300 font-medium">
          Ihr Browser oder Gerät unterstützt keine Passkeys. Auf iPhone / iPad / Mac
          nutzen Sie bitte Safari, auf Android Chrome, auf Windows den Edge-Browser
          mit aktiviertem Windows Hello.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3" data-testid="passkey-settings">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Fingerprint className="text-[#00E575]" size={16} />
          <h2 className="font-display text-sm font-semibold text-zinc-400 uppercase tracking-widest">
            Passkeys / Face ID · Touch ID
          </h2>
        </div>
        <Button
          data-testid="passkey-add-btn"
          onClick={handleEnroll}
          disabled={enrolling}
          size="sm"
          className="h-8 bg-[#00E575] text-[#09090B] font-semibold hover:bg-[#00C964] active:scale-[0.97]"
        >
          {enrolling ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Neuer Passkey</>}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-zinc-500">
          <Loader2 size={18} className="animate-spin" />
        </div>
      ) : creds.length === 0 ? (
        <p className="text-sm text-zinc-400 leading-relaxed">
          Sie haben noch keinen Passkey hinterlegt. Tippen Sie auf <strong>Neuer Passkey</strong>,
          um sich beim nächsten Login direkt per Face ID, Touch ID oder Windows Hello
          anzumelden — ganz ohne SMS- oder E-Mail-Code.
        </p>
      ) : (
        <ul className="space-y-2">
          {creds.map((c) => (
            <li
              key={c.id}
              data-testid={`passkey-row-${c.id}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-[#0a0a0c] border border-zinc-800"
            >
              <div className="w-9 h-9 rounded-lg bg-[#00E575]/10 flex items-center justify-center shrink-0">
                <Smartphone className="text-[#00E575]" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-100 truncate">{c.label}</div>
                <div className="text-[12px] text-zinc-300 font-medium">
                  Angelegt: {c.created_at ? new Date(c.created_at).toLocaleDateString("de-DE") : "—"}
                  {c.last_used_at && (
                    <> · Zuletzt benutzt: {new Date(c.last_used_at).toLocaleDateString("de-DE")}</>
                  )}
                </div>
              </div>
              <button
                data-testid={`passkey-delete-${c.id}`}
                onClick={() => handleDelete(c)}
                title="Passkey löschen"
                className="p-2 rounded-lg text-red-400 hover:bg-red-500/15 hover:text-red-300 transition"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
